/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Code, Download, Workspaces } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    Typography
} from '@mui/material';

/*  Components  */
import ImportantResources from 'application/Home/Partner/Integrations/Pages/Components/ImportantResources';
import { MobileConfigDownload } from 'application/Home/Partner/Integrations/Pages/Components/MobileConfigDownload';
import IntegrationSteps from 'application/Home/Partner/Integrations/Pages/Components/Steps';

/* Constants */
import IntegrationScriptFilename from 'application/Home/Partner/Integrations/Pages/Constants/IntegrationScriptsFilename.json';

/* Helpers */
import { getScript } from 'application/Home/Partner/Integrations/Pages/Helpers/utils';

/*  Shared  */
import { PartnerBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { AddClientModal } from 'application/Shared/Components/Client';
import CodeBlockToken from 'application/Shared/Components/CodeBlock/Token';
import { TenantPicker } from 'application/Shared/Components/Pickers';
import { FontSizes, URLs } from 'application/Shared/Constants';
import { Notify, SessionHelper } from 'application/Shared/Helpers';
import { ModalFormFooter } from 'application/Shared/Modals';
import { Token } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

import { SitePicker } from '../Pickers/Site';

class CustomScript extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientId: '0',
            loaded: false,
            macManual: [],
            macMDM: [],
            macMDMScriptOnly: [],
            partnerId: '',
            partnerMac: [],
            partnerToken: '',
            partnerWin: [],
            showClientScripts: true,
            showModal: false,
            showPartnerScripts: false,
            token: '',
            useAnIntegrationMethod: 'false',
            userTheme: 'system',
            windows: []
        };

        /* requests */
        this.Token = new Token();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        const { session, match, clientToken } = this.props;
        const { partnerId } = match.params;
        const userTheme = getUserTheme(session?.user?.theme);

        this.setState({ userTheme });
        const sessionHelper = new SessionHelper(session);
        // If no partnerId in the URL, use session tenant as fallback.
        const tenantId = partnerId || sessionHelper.getTenantId();

        this.setState({ partnerId: tenantId }, async () => {
            const partnerToken = await this.handleClient({
                clientId: clientToken ? match.params.clientId : tenantId,
                startWithoutTenantSelectedInPicker: !clientToken
            });

            if (this.unmount) {
                return;
            }

            this.setState({ partnerToken });
            this.makePartnerMac(partnerToken);
            this.makePartnerWin(partnerToken);
        });
    };

    unload = () => {
        this.unmount = true;
        this.Token.cancel();
    };

    handleClient = async (p) => {
        if (this.unmount) {
            return false;
        }
        const { clientToken } = this.props;

        const { partnerId } = this.state;
        const { clientId, startWithoutTenantSelectedInPicker } = p;

        this.setState({ clientId });

        if (clientId === 'new') {
            this.setState({ showModal: true });
            return false;
        }

        const params = {};
        if (clientId !== partnerId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const token = clientToken || (await this.Token.getToken(params));

            if (!token) {
                const err = 'No Token found for this Client';
                debug.e(err);
                Notify.e(err);
                return false;
            }

            this.setState(
                { token: startWithoutTenantSelectedInPicker ? null : token },
                () => {
                    this.makeScripts();
                }
            );

            return token;
        } catch (e) {
            return false;
        }
    };

    makeScripts = () => {
        this.makeWindows();
        this.makeMacManual();
        this.makeMacMDM();
        this.makeMacMDMScriptOnlyAddigy();
    };

    makeWindows = async () => {
        if (this.unmount) {
            return;
        }

        const { t, forConnectWise } = this.props;
        const { token } = this.state;

        const filenamePath = forConnectWise
            ? IntegrationScriptFilename.connectwiseScript
            : IntegrationScriptFilename.customScriptWindows;

        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token
        });
        const windows = [
            {
                component: (
                    <Typography
                        dangerouslySetInnerHTML={{
                            __html: t('Integrations.Custom.makeWindows.step1', {
                                filename
                            })
                        }}
                    />
                ),
                copyValue: scriptCopyValue,
                download: filename,
                step: true
            }
        ];

        if (!this.unmount) {
            this.setState({ windows });
        }
    };

    makeMacManual = async () => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;
        const { token } = this.state;

        const filenamePath = IntegrationScriptFilename.customScriptMacManual;

        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token
        });

        const macManual = [
            {
                component: (
                    <Typography style={{ fontStyle: 'italic' }}>
                        {t('Integrations.Custom.makeMacManual.info')}
                    </Typography>
                ),
                step: false
            },
            {
                component: (
                    <MobileConfigDownload
                        stepText={t('Integrations.Custom.makeMacManual.step1')}
                    />
                ),
                step: true
            },
            {
                component: t('Integrations.Custom.makeMacManual.step2'),
                copyValue: scriptCopyValue,
                download: filename,
                step: true
            }
        ];

        if (!this.unmount) {
            this.setState({ macManual });
        }
    };

    makeMacMDM = async () => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;
        const { token } = this.state;

        const filenamePath = IntegrationScriptFilename.customScriptMacMDM;

        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token
        });

        const macMDM = [
            {
                component: (
                    <MobileConfigDownload
                        stepText={t('Integrations.Custom.makeMacMDM.step1')}
                    />
                ),
                step: true
            },
            {
                component: (
                    <Box as="span" display="flex" alignItems="center">
                        <span>{t('Integrations.Custom.makeMacMDM.step2')}</span>
                        <a
                            href={`${URLs.publicFiles}/Saaslio.pkg`}
                            data-trackid="customScript_macMDM_download_file"
                        >
                            <Button
                                data-trackid="customScript_macMDM_download"
                                variant="contained"
                                color="primary"
                                style={{ marginLeft: '8px' }}
                                size="small"
                            >
                                <Download
                                    style={{
                                        fontSize: FontSizes[2],
                                        marginRight: '4px'
                                    }}
                                />
                                {t(
                                    'Integrations.Custom.makeMacMDM.step2.filename'
                                )}
                            </Button>
                        </a>
                    </Box>
                ),
                step: true
            },
            {
                component: t('Integrations.Custom.makeMacMDM.step3'),
                step: true
            },
            {
                component: t('Integrations.Custom.makeMacMDM.step4'),
                copyValue: scriptCopyValue,
                download: filename,
                step: true
            }
        ];

        if (!this.unmount) {
            this.setState({ macMDM });
        }
    };

    makeMacMDMScriptOnlyAddigy = async () => {
        if (this.unmount) {
            return;
        }

        const { forAddigy, forJamf, t } = this.props;
        const { token } = this.state;

        const filenamePath = IntegrationScriptFilename.customScriptMacMDM;

        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token
        });

        let text = '';
        if (forAddigy) {
            text = t('Integrations.addigy.step11');
        }
        if (forJamf) {
            text = t('Integrations.jamf.step14');
        }

        const macMDMScriptOnly = [
            {
                component: (
                    <Typography
                        dangerouslySetInnerHTML={{
                            __html: text
                        }}
                    />
                ),
                copyValue: scriptCopyValue,
                download: filename,
                step: false
            }
        ];

        if (!this.unmount) {
            this.setState({ macMDMScriptOnly });
        }
    };

    makePartnerMac = async (partnerToken) => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;

        const filenamePath = IntegrationScriptFilename.customScriptPartnerMac;
        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token: partnerToken
        });

        const partnerMac = [
            {
                component: (
                    <MobileConfigDownload
                        stepText={t('Integrations.Custom.makePartnerMac.step1')}
                    />
                ),
                step: true
            },
            {
                component: (
                    <Box as="span" display="flex" alignItems="center">
                        <span>
                            {t('Integrations.Custom.makePartnerMac.step2')}
                        </span>
                        <a
                            href={`${URLs.publicFiles}/Saaslio.pkg`}
                            data-trackid="customScript_partnerMac_download_file"
                        >
                            <Button
                                data-trackid="customScript_partnerMac_download"
                                variant="contained"
                                color="primary"
                                style={{ marginLeft: '8px' }}
                                size="small"
                            >
                                <Download
                                    style={{
                                        fontSize: FontSizes[2],
                                        marginRight: '4px'
                                    }}
                                />
                                {t(
                                    'Integrations.Custom.makePartnerMac.step2.filename'
                                )}
                            </Button>
                        </a>
                    </Box>
                ),
                step: true
            },
            {
                component: t('Integrations.Custom.makePartnerMac.step3'),
                copyValue: scriptCopyValue,
                download: filename,
                step: true
            }
        ];

        if (!this.unmount) {
            this.setState({ loaded: true, partnerMac });
        }
    };

    makePartnerWin = async (partnerToken) => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;

        const filenamePath = IntegrationScriptFilename.customScriptParnerWin;

        const filename = filenamePath.slice(filenamePath.lastIndexOf('/') + 1);

        const scriptCopyValue = await getScript({
            filename: filenamePath,
            token: partnerToken
        });

        const partnerWin = [
            {
                component: (
                    <Typography
                        dangerouslySetInnerHTML={{
                            __html: t(
                                'Integrations.Custom.makePartnerWin.step1'
                            )
                        }}
                    />
                ),
                copyValue: scriptCopyValue,
                download: filename,
                step: true
            }
        ];

        if (!this.unmount) {
            this.setState({ loaded: true, partnerWin });
        }
    };

    renderWindows = () => {
        const { userTheme, windows } = this.state;
        const { forConnectWise, t } = this.props;

        return (
            <Box width="100%" data-testid="customScriptRenderWindows">
                {!forConnectWise && (
                    <Box mb="16px">
                        <img
                            src={`/images/${userTheme}/integrations/windows_logo.png`}
                            alt={t('Common.windows.logo')}
                            style={{ width: '200px' }}
                        />
                    </Box>
                )}
                <IntegrationSteps steps={windows} />
            </Box>
        );
    };

    renderMacManual = () => {
        const { t } = this.props;
        const { macManual, userTheme } = this.state;

        return (
            <Box
                display="flex"
                flexDirection="column"
                width="100%"
                data-testid="customScriptRenderMacManual"
            >
                <Box display="flex" alignItems="center" my="16px">
                    <img
                        src={`/images/${userTheme}/integrations/apple_outlined.png`}
                        alt={t('Common.mac.logo')}
                        style={{ width: '48px' }}
                    />
                    <Typography
                        variant="h4"
                        flex={1}
                        style={{ marginLeft: '16px' }}
                    >
                        {t('Integrations.Custom.makeMacManual.title')}
                    </Typography>
                </Box>
                <IntegrationSteps steps={macManual} />
            </Box>
        );
    };

    renderMacMDM = () => {
        const { forAcronis, forAddigy, forJamf, t } = this.props;
        const { macMDM, macMDMScriptOnly, userTheme } = this.state;

        return (
            <Box
                display="flex"
                flexDirection="column"
                width="100%"
                data-testid="customScriptRenderMacMDM"
            >
                {!forAddigy && !forJamf && !forAcronis ? (
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            my="16px"
                            data-testid="customScriptRenderMacMDMTitle"
                        >
                            <img
                                src={`/images/${userTheme}/integrations/apple_full.png`}
                                alt={t('Common.mac.logo')}
                                style={{ width: '48px' }}
                            />
                            <Typography
                                variant="h4"
                                flex={1}
                                style={{ marginLeft: '16px' }}
                            >
                                {t('Integrations.Custom.makeMacMDM.title')}
                            </Typography>
                        </Box>
                        <IntegrationSteps steps={macMDM} />
                    </>
                ) : (
                    <IntegrationSteps steps={macMDMScriptOnly} />
                )}
            </Box>
        );
    };

    renderPartnerSummary = () => {
        const { t } = this.props;
        const { partnerToken } = this.state;
        return (
            <>
                <Box mb="16px">
                    <Typography style={{ fontStyle: 'italic' }}>
                        {t('Integrations.Custom.makePartner.info')}
                    </Typography>
                </Box>
                <Box>
                    <CodeBlockToken token={partnerToken} height="56" partner />
                </Box>
            </>
        );
    };

    renderPartnerMac = () => {
        const { t } = this.props;
        const { partnerMac, userTheme } = this.state;

        return (
            <>
                <Box display="flex" alignItems="center" my="16px">
                    <img
                        src={`/images/${userTheme}/integrations/apple_outlined.png`}
                        alt={t('Common.mac.logo')}
                        style={{ width: '48px' }}
                    />
                    <Typography
                        variant="h4"
                        flex={1}
                        style={{ marginLeft: '16px' }}
                    >
                        {t('Integrations.Custom.makePartnerMac.title')}
                    </Typography>
                </Box>
                <IntegrationSteps steps={partnerMac} />
            </>
        );
    };

    renderPartnerWin = () => {
        const { t } = this.props;
        const { partnerWin, userTheme } = this.state;

        return (
            <>
                <Grid item xs={12}>
                    <img
                        src={`/images/${userTheme}/integrations/windows_logo.png`}
                        alt={t('Common.windows.logo')}
                        style={{ width: '200px' }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <IntegrationSteps steps={partnerWin} />
                </Grid>
            </>
        );
    };

    renderClientSelect = () => {
        const { clientToken, disableClientSelect, setClientId, t } = this.props;
        const { token, reloadClients } = this.state;
        let clientId = this.props.clientId || this.state.clientId;

        if (
            !token &&
            this.props.clientId &&
            this.props.clientId !== '0' &&
            this.props.clientId !== this.state.clientId
        )
            this.handleClient({ clientId: this.props.clientId });

        if (!token && !this.props.clientId) {
            clientId = '0';
        }

        return (
            <Grid
                container
                data-testid="customScriptClientSelect"
                my="16px"
                spacing={2}
            >
                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.client')}
                        </Typography>
                        <Box mt="8px" width="100%">
                            <TenantPicker
                                allowAdd
                                allValue={t('Common.choose_a_client')}
                                allValueId="0"
                                color="secondary"
                                disabled={
                                    Boolean(clientToken) || disableClientSelect
                                }
                                onChange={(v) => {
                                    this.handleClient({ clientId: v });
                                    if (setClientId) setClientId(v);
                                }}
                                reload={reloadClients}
                                value={clientId}
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Grid>
                {token && (
                    <Grid item xs={12} md={6}>
                        <CodeBlockToken token={token} />
                    </Grid>
                )}
            </Grid>
        );
    };

    renderSiteSelect = () => {
        const { site, setSite, disableSelection, t } = this.props;
        const { clientId } = this.props || this.state;

        return (
            <Grid
                container
                data-testid="unifiedAgentSiteSelect"
                my="16px"
                spacing={2}
            >
                <Grid item xs={12} md={6}>
                    <Box>
                        <Typography style={{ fontWeight: 600 }}>
                            {t('Common.site')}
                        </Typography>
                        <Box mt="8px" width="100%">
                            <SitePicker
                                allValue={t('Common.chooseSite')}
                                allValueId="0 "
                                clientId={clientId}
                                color="secondary"
                                disabled={disableSelection}
                                onChange={(v) => setSite(v)}
                                setSite={setSite}
                                value={site}
                                variant="outlined"
                                type="ANM"
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        );
    };

    renderClientSection = () => {
        const { clientToken, t } = this.props;
        const { token } = this.state;

        return (
            <Box data-testid="customScriptSectionsClient">
                <Box mb="32px">
                    <ImportantResources />
                </Box>
                {!clientToken && (
                    <Alert severity="info" icon={false}>
                        {t('Notice.CustomScripts.select_client')}
                    </Alert>
                )}
                {this.renderClientSelect()}
                {token && (
                    <Box mt="32px">
                        <Box mt="16px">{this.renderWindows()}</Box>
                        <Box mt="32px">{this.renderMacManual()}</Box>
                        <Box mt="16px">{this.renderMacMDM()}</Box>
                    </Box>
                )}
            </Box>
        );
    };

    renderPartnerSection = () => (
        <Box data-testid="customScriptSectionsPartner">
            <Box mb="32px">
                <ImportantResources />
            </Box>
            <Box mb="32px">{this.renderPartnerSummary()}</Box>
            <Box>
                <Box mt="16px">{this.renderPartnerWin()}</Box>
                <Box mt="32px">{this.renderPartnerMac()}</Box>
            </Box>
        </Box>
    );

    renderSections = () => {
        const { t } = this.props;
        const { showClientScripts, showPartnerScripts } = this.state;

        return (
            <Box data-testid="customScriptSections">
                <Box width="100%" mb="16px">
                    <Accordion
                        expanded={showClientScripts}
                        onChange={(_e, v) => {
                            this.setState({ showClientScripts: v });
                        }}
                        slotProps={{ transition: { unmountOnExit: true } }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <IconWithVariant
                                    data-testid="expandAccordionIconCustomScript"
                                    variant="primary"
                                    src="ChevronDown"
                                    size={FontSizes[2]}
                                />
                            }
                        >
                            <Box display="flex" alignItems="center">
                                <Code
                                    style={{
                                        color: 'icon.primary',
                                        marginRight: '8px'
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('Integrations.client_scripts')}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {this.renderClientSection()}
                        </AccordionDetails>
                    </Accordion>
                </Box>
                <Box width="100%">
                    <Accordion
                        expanded={showPartnerScripts}
                        onChange={(_e, v) => {
                            this.setState({ showPartnerScripts: v });
                        }}
                        slotProps={{ transition: { unmountOnExit: true } }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <IconWithVariant
                                    data-testid="expandAccordionIconCustomScript"
                                    variant="primary"
                                    src="ChevronDown"
                                    size={FontSizes[2]}
                                />
                            }
                        >
                            <Box display="flex" alignItems="center">
                                <Workspaces
                                    sx={{
                                        color: 'icon.primary',
                                        marginRight: '8px'
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('Integrations.partner_scripts')}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {this.renderPartnerSection()}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
        );
    };

    render() {
        // a) Onboarding === true will show the accordions, client and partner.
        // b) forIntune || forConnectWise will not show accordions, just what is inside the
        // client accordion, but the windows section only. Will show "pick your client" alert.
        // c) forAddigy || forJamf will not show accordions, just what is inside the
        // client accordion, but the mac section only.
        // d) Having clientToken means we are at the admin level with a defined customer already, therefore
        // just show the client scripts with that particular token, no accordions, no partner section.
        const {
            clientToken,
            forAcronis,
            forAddigy,
            forConnectWise,
            forIntune,
            forJamf,
            forUnifiedAgent,
            onboarding,
            t
        } = this.props;
        const { loaded, showModal, token, useAnIntegrationMethod } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Box data-testid="customScript">
                {onboarding &&
                    !(
                        forAcronis ||
                        forAddigy ||
                        forIntune ||
                        forConnectWise ||
                        forJamf ||
                        forUnifiedAgent
                    ) &&
                    !clientToken &&
                    this.renderSections()}
                {clientToken && this.renderClientSection()}
                {(forIntune || forConnectWise || forAcronis) && (
                    <Grid
                        container
                        spacing={3}
                        mt="2px"
                        data-testid="customScriptForIntuneOrCW"
                    >
                        <Grid item xs={12}>
                            <Alert severity="info" icon={false}>
                                {t('Notice.CustomScripts.select_client')}
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            {this.renderClientSelect()}
                        </Grid>
                        <Grid item xs={12}>
                            {token && this.renderWindows()}
                        </Grid>
                    </Grid>
                )}
                {(forAddigy || forJamf) && (
                    <Box data-testid="customScriptForAddigyJamf" mt="2px">
                        {this.renderClientSelect()}
                        {token && this.renderMacMDM()}
                    </Box>
                )}
                {forUnifiedAgent && (
                    <Box data-testid="customScriptForUnifiedAgent" mt="2px">
                        <Box>
                            <ImportantResources />
                        </Box>
                        <Box>
                            {!clientToken && (
                                <Alert severity="info" icon={false}>
                                    {t('Notice.CustomScripts.select_client')}
                                </Alert>
                            )}
                        </Box>
                        <Box mt="8px">{this.renderClientSelect()}</Box>
                        <Box mt="8px">{this.renderSiteSelect()}</Box>
                    </Box>
                )}
                {!onboarding &&
                    !(
                        forAcronis ||
                        forIntune ||
                        forConnectWise ||
                        forAddigy ||
                        forJamf ||
                        forUnifiedAgent
                    ) &&
                    !clientToken && (
                        <Box data-testid="customScriptNotOnboardingIntuneCWAddigy">
                            <Box width="100%">
                                <Typography variant="h3">
                                    {t('Integrations.Custom.title')}
                                </Typography>
                                <hr />
                            </Box>
                            <PartnerBackButton
                                linkTo="/integrations/partnerLevel"
                                textOverride="Integrations.Common.back"
                            />
                            <Paper
                                elevation={0}
                                variant="outlined"
                                style={{ padding: '20px' }}
                            >
                                {this.renderSections()}
                            </Paper>
                        </Box>
                    )}
                {showModal && (
                    <AddClientModal
                        formFooterComponent={
                            <ModalFormFooter
                                onClose={() =>
                                    this.setState({
                                        showModal: false
                                    })
                                }
                            />
                        }
                        handleUseAnIntegrationMethodChange={(value) => {
                            this.setState({
                                useAnIntegrationMethod: value
                            });
                        }}
                        onboarding={onboarding}
                        onSuccess={(clientId) => {
                            this.setState({ showModal: false });

                            if (clientId) {
                                this.setState({ reloadClients: true }, () => {
                                    this.setState({
                                        reloadClients: false
                                    });
                                    this.handleClient({ clientId });
                                });
                            }
                        }}
                        show={showModal}
                        showDescription
                        useAnIntegrationMethod={
                            onboarding ? 'false' : useAnIntegrationMethod
                        }
                    />
                )}
            </Box>
        );
    }
}
export default withRouter(withTranslation()(CustomScript));
